import type { OrderLineDto, OrderListItem, SupplierRoundSummary } from "@dishour/shared";
import { Link } from "react-router";
import { useState } from "react";
import {
  estimatedRevenue,
  useAcceptOrder,
  useCancelRound,
  useConfirmRound,
  useFinalizeOrder,
  useRejectOrder,
  useSupplierDashboard,
  useSupplierOrders,
} from "../../api/supplier.js";
import { AppShell } from "../../components/AppShell.js";
import { EmptyState } from "../../components/EmptyState.js";
import { inputClass } from "../../components/Field.js";
import { ProgressBar } from "../../components/ProgressBar.js";
import { Skeleton } from "../../components/Skeleton.js";
import { CommitmentStatusBadge, OrderStatusBadge, RoundStatusBadge } from "../../components/StatusBadge.js";
import { formatDateTime, formatMoney, formatQty, formatRelativeDeadline } from "../../lib/format.js";
import { toThousandths } from "../../lib/quantity.js";

/** A deal without a SKU is free-text stock — treated as a plain whole-unit count for display. */
const unitOf = (deal: { sku: { unit: string; isDivisible: boolean } | null }) =>
  deal.sku ?? { unit: "개", isDivisible: false };

function RoundCard({ round }: { round: SupplierRoundSummary }) {
  const confirmRound = useConfirmRound();
  const cancelRound = useCancelRound();
  const busy = confirmRound.isPending || cancelRound.isPending;
  const error = confirmRound.error ?? cancelRound.error;

  // Distinct from committedQty (soft + confirmed participation): only the confirmed portion
  // is what will actually ship once this round is confirmed.
  const hasConfirmed = toThousandths(round.confirmedQty) > 0;

  return (
    <li className="mb-4 rounded-xl border border-stone-200 bg-white p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">{round.sku.nameKo}</h2>
          <p className="text-sm text-stone-500">
            {formatMoney(round.dealUnitPrice)} / {round.sku.unit} · 마감 {formatDateTime(round.closesAt)}
          </p>
        </div>
        <RoundStatusBadge status={round.status} />
      </div>

      {/* Leads with the number a supplier scans for first: what this round is worth. */}
      <p className="mb-1 text-3xl font-bold text-emerald-700">
        {formatMoney(estimatedRevenue(round.committedQty, round.dealUnitPrice))}
      </p>
      <p className="mb-3 text-sm text-stone-500">
        예상 매출 · 참여 식당 {round.participants.length}곳
        {hasConfirmed ? ` · 확정분 ${formatMoney(estimatedRevenue(round.confirmedQty, round.dealUnitPrice))}` : ""}
      </p>

      <ProgressBar percent={round.progressPercent} met={round.thresholdMet} />
      <p className="mt-1 mb-3 text-sm text-stone-600">
        취합 {formatQty(round.committedQty, round.sku)} / 목표 {formatQty(round.thresholdQty, round.sku)} ·{" "}
        {formatRelativeDeadline(round.closesAt)}
      </p>

      <table className="mb-3 w-full text-sm">
        <caption className="sr-only">참여 식당별 신청 수량과 확인 상태</caption>
        <thead>
          <tr className="border-b border-stone-200 text-left text-stone-500">
            <th className="py-1 font-medium">식당</th>
            <th className="py-1 font-medium">수량</th>
            <th className="py-1 font-medium">상태</th>
          </tr>
        </thead>
        <tbody>
          {round.participants.map((p) => (
            <tr key={p.commitmentId} className="border-b border-stone-100">
              <td className="py-1">{p.restaurantName}</td>
              <td className="py-1">{formatQty(p.qty, round.sku)}</td>
              <td className="py-1">
                <CommitmentStatusBadge status={p.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {round.status === "confirming" ? (
        <p className="mb-2 text-xs text-amber-700">
          확정하면 아직 최종 확인하지 않은 식당은 이번 물량에서 빠집니다 — 확정 시 물량{" "}
          {formatQty(round.confirmedQty, round.sku)}로 진행됩니다.
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mb-2 text-sm text-red-600">
          {(error as Error).message}
        </p>
      ) : null}

      <div className="flex gap-2">
        {round.status === "confirming" ? (
          <button
            type="button"
            className="flex-1 rounded-lg bg-emerald-700 px-4 py-3 font-semibold text-white disabled:opacity-50"
            disabled={busy}
            onClick={() => confirmRound.mutate(round.id)}
          >
            이 물량으로 확정
          </button>
        ) : null}
        {round.status === "open" || round.status === "confirming" ? (
          <button
            type="button"
            className="rounded-lg border border-stone-300 px-4 py-3 text-sm disabled:opacity-50"
            disabled={busy}
            onClick={() => cancelRound.mutate(round.id)}
          >
            취소
          </button>
        ) : null}
        {round.status === "confirmed" ? (
          <Link
            to={`/supplier/rounds/${round.id}/finalize`}
            className="flex-1 rounded-lg bg-brand-700 px-4 py-3 text-center font-semibold text-white hover:bg-brand-800"
          >
            납품 확정 · 청구서 발행
          </Link>
        ) : null}
      </div>
    </li>
  );
}

function PendingOrderCard({ order }: { order: OrderListItem }) {
  const accept = useAcceptOrder();
  const reject = useRejectOrder();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const busy = accept.isPending || reject.isPending;
  const error = accept.error ?? reject.error;

  return (
    <li className="mb-4 rounded-xl border border-stone-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium">{order.restaurant.name}</span>
        <OrderStatusBadge status={order.status} />
      </div>
      <ul className="mb-3 text-sm text-stone-600">
        {order.lines.map((line) => (
          <li key={line.id}>
            {line.sku.nameKo} · {formatQty(line.qty, line.sku)} · {formatMoney(line.unitPrice)}
          </li>
        ))}
      </ul>
      {error ? (
        <p role="alert" className="mb-2 text-sm text-red-600">
          {(error as Error).message}
        </p>
      ) : null}
      {rejecting ? (
        <div className="flex flex-col gap-2">
          <input
            aria-label="거절 사유"
            className={inputClass}
            placeholder="거절 사유 (선택)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              type="button"
              className="flex-1 rounded-lg bg-red-600 px-4 py-3 font-semibold text-white disabled:opacity-50"
              disabled={busy}
              onClick={() => reject.mutate({ id: order.id, reason: reason || null })}
            >
              거절 확정
            </button>
            <button
              type="button"
              className="rounded-lg border border-stone-300 px-4 py-3 text-sm"
              onClick={() => setRejecting(false)}
            >
              취소
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            className="flex-1 rounded-lg bg-brand-700 px-4 py-3 font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            disabled={busy}
            onClick={() => accept.mutate(order.id)}
          >
            수락
          </button>
          <button
            type="button"
            className="rounded-lg border border-stone-300 px-4 py-3 text-sm disabled:opacity-50"
            disabled={busy}
            onClick={() => setRejecting(true)}
          >
            거절
          </button>
        </div>
      )}
    </li>
  );
}

function AcceptedOrderCard({ order }: { order: OrderListItem }) {
  const finalize = useFinalizeOrder();
  const [actual, setActual] = useState<Record<string, string>>({});
  const valueFor = (line: OrderLineDto) => actual[line.id] ?? line.qty;

  return (
    <li className="mb-4 rounded-xl border border-stone-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium">{order.restaurant.name}</span>
        <OrderStatusBadge status={order.status} />
      </div>
      <p className="mb-4 text-sm text-stone-600">
        실제로 납품한 수량을 입력해 주세요. 청구서는 이 수량으로 발행됩니다. 공급하지 못한 곳은 0을
        입력하면 청구서가 만들어지지 않습니다.
      </p>
      <ul className="mb-3">
        {order.lines.map((line) => {
          const value = valueFor(line);
          const noDelivery = Number(value) === 0;
          return (
            <li key={line.id} className="border-b border-stone-100 py-2">
              <div className="mb-1 flex justify-between text-sm">
                <span>{line.sku.nameKo}</span>
                <span className="text-stone-500">주문 {formatQty(line.qty, line.sku)}</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  aria-label={`${line.sku.nameKo} 실납품 수량`}
                  inputMode="decimal"
                  className={inputClass}
                  value={value}
                  onChange={(e) => setActual({ ...actual, [line.id]: e.target.value })}
                />
                <span>{line.sku.unit}</span>
              </div>
              {noDelivery ? <p className="mt-1 text-xs text-amber-700">0 입력 시 이 품목은 청구되지 않습니다.</p> : null}
            </li>
          );
        })}
      </ul>
      {finalize.error ? (
        <p role="alert" className="mb-2 text-sm text-red-600">
          {(finalize.error as Error).message}
        </p>
      ) : null}
      <button
        type="button"
        className="w-full rounded-lg bg-brand-700 px-4 py-3 font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
        disabled={finalize.isPending}
        onClick={() =>
          finalize.mutate({
            id: order.id,
            lines: order.lines.map((line) => ({ orderLineId: line.id, fulfilledQty: valueFor(line) })),
          })
        }
      >
        {finalize.isPending ? "발행 중…" : "납품 확정하고 청구서 발행"}
      </button>
    </li>
  );
}

function SupplierOrdersSection() {
  const orders = useSupplierOrders();
  const pending = orders.data?.filter((o) => o.status === "pending") ?? [];
  const accepted = orders.data?.filter((o) => o.status === "accepted") ?? [];

  if (orders.isPending) return <p className="text-stone-500">불러오는 중…</p>;
  if (orders.error) {
    return (
      <p role="alert" className="text-red-600">
        목록을 불러오지 못했습니다.
      </p>
    );
  }
  if (pending.length === 0 && accepted.length === 0) return null;

  return (
    <>
      {pending.length > 0 ? (
        <>
          <h2 className="mt-6 mb-2 font-semibold">대기중인 주문</h2>
          <ul>{pending.map((o) => <PendingOrderCard key={o.id} order={o} />)}</ul>
        </>
      ) : null}
      {accepted.length > 0 ? (
        <>
          <h2 className="mt-6 mb-2 font-semibold">납품 확정 대기</h2>
          <ul>{accepted.map((o) => <AcceptedOrderCard key={o.id} order={o} />)}</ul>
        </>
      ) : null}
    </>
  );
}

export function SupplierDashboardPage() {
  const { data, isPending, error } = useSupplierDashboard();

  return (
    <AppShell title="이번 주 취합 현황">
      {isPending ? <Skeleton /> : null}
      {error ? (
        <p role="alert" className="text-red-600">
          목록을 불러오지 못했습니다.
        </p>
      ) : null}

      {data && data.rounds.length === 0 ? <EmptyState message="진행 중인 공동구매가 없습니다." /> : null}

      <ul>{data?.rounds.map((round) => <RoundCard key={round.id} round={round} />)}</ul>

      <h2 className="mt-6 mb-2 font-semibold">주문 관리</h2>
      <SupplierOrdersSection />

      {data && data.deals.length > 0 ? (
        <>
          <h2 className="mt-6 mb-2 font-semibold">내 특가</h2>
          <ul>
            {data.deals.map((deal) => (
              <li key={deal.id} className="border-b border-stone-200 py-2 text-sm">
                {deal.title} · {formatMoney(deal.unitPrice)} · 신청 {formatQty(deal.qtyClaimed, unitOf(deal))}/
                {formatQty(deal.qtyAvailable, unitOf(deal))}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </AppShell>
  );
}
